(() => {
  const TABS_SELECTOR = "[data-metafield-tabs]";
  const CART_ENDPOINT = "/cart.js";

  /* ── Helpers ── */
  const parseJSON = (raw, fallback) => {
    try { return JSON.parse(raw || fallback); }
    catch { return JSON.parse(fallback); }
  };

  const formatMoney = (cents, locale, currency) => {
    const value = (Number(cents) || 0) / 100;
    try { return new Intl.NumberFormat(locale, { style: "currency", currency }).format(value); }
    catch { return value.toFixed(2); }
  };
  const getBottleMultiplier = (item) => {
    const variantTitle = String(item?.variant_title || "").toLowerCase();
    if (variantTitle.includes("single bottle")) return 1;
    if (variantTitle.includes("half case")) return 3;
    if (variantTitle.includes("case (6)") || variantTitle.includes("case of 6"))  return 6;

    const parenMatch = variantTitle.match(/\((\d+)\)/);
    if (parenMatch) return Number(parenMatch[1]) || 1;

    return 1;
  };

  const calcTierPrice = (unitPriceCents, quantity, discountPercent) => {
    const total = unitPriceCents * quantity;
    return discountPercent > 0 ? Math.round(total * (100 - discountPercent) / 100) : total;
  };
  const applyPlanDiscount = (price, plan) => {
    if (!plan) return null;
    const discountType = String(plan.discount_type || "");
    const discountValue = Number(plan.discount_value || 0);

    if (discountType === "percentage" && discountValue > 0) {
      return Math.round(price * (100 - discountValue) / 100);
    } else if (discountType === "fixed_amount" && discountValue > 0) {
      return Math.max(0, price - discountValue);
    } else if (discountType === "price" && discountValue > 0) {
      return Math.max(0, discountValue);
    }
    return null;
  };

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const $ = (el, sel) => el.querySelector(sel);
  const $$ = (el, sel) => el.querySelectorAll(sel);

  /* ── JSON cache — avoids re-parsing data-variants / data-selling-plans on every interaction ── */
  const jsonCache = new WeakMap();
  const getCachedJSON = (el, attr, fallback = "[]") => {
    let map = jsonCache.get(el);
    if (!map) { map = {}; jsonCache.set(el, map); }
    if (!(attr in map)) map[attr] = parseJSON(el.dataset[attr], fallback);
    return map[attr];
  };

  /* ── Section replace for cross-product navigation (cached) ── */
  const sectionCache = new Map();
  const replaceMainProductSection = async (tabs, productUrl, activeTab) => {
    const section = tabs.closest(".main-product.section__container");
    if (!section || !productUrl) return;
    // Remember which tab was active so we can restore it after re-init
    const preserveTab = activeTab || tabs.querySelector("[data-tab-trigger].is-active")?.dataset.tabTrigger;
    tabs.classList.add("is-loading");
    try {
      let html = sectionCache.get(productUrl);
      if (!html) {
        html = await fetch(productUrl, { credentials: "same-origin" }).then((r) => r.text());
        sectionCache.set(productUrl, html);
      }
      const next = new DOMParser().parseFromString(html, "text/html");
      const nextSection = next.querySelector(".main-product.section__container");
      if (!nextSection) return;
      section.innerHTML = nextSection.innerHTML;
      // Carry over inline style (--color-prod-background, --color-prod-title, etc.)
      if (nextSection.getAttribute("style")) {
        section.setAttribute("style", nextSection.getAttribute("style"));
      } else {
        section.removeAttribute("style");
      }
      if (next.title) document.title = next.title;
      // Update browser URL to reflect the new product
      const cleanUrl = productUrl.split("?")[0];
      if (cleanUrl && window.location.pathname !== cleanUrl) {
        window.history.pushState({ stitcher: true }, next.title || "", cleanUrl);
      }
      // Re-execute inline scripts from the replaced section (app blocks, etc.)
      section.querySelectorAll("script").forEach((oldScript) => {
        const newScript = document.createElement("script");
        [...oldScript.attributes].forEach((a) => newScript.setAttribute(a.name, a.value));
        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
      });
      window.ProductStitcherTabs.initAll(section);
      // Notify Shopify apps (subscription widgets, etc.) to re-initialise
      const shopifySection = section.closest(".shopify-section") || section;
      shopifySection.dispatchEvent(new CustomEvent("shopify:section:load", { bubbles: true, detail: { sectionId: shopifySection.id } }));
      // Restore the previously active tab
      if (preserveTab) {
        const newTabs = $(section, TABS_SELECTOR);
        if (newTabs) {
          const trigger = newTabs.querySelector(`[data-tab-trigger="${preserveTab}"]`);
          if (trigger) trigger.click();
        }
      }
      document.dispatchEvent(new CustomEvent("product:stitcher:section-replaced", { detail: { productUrl } }));
    } catch { tabs.classList.remove("is-loading"); }
  };

  /* ── Cart fetch (single in-flight request) ── */
  let cartPromise = null;
  const fetchCart = () => {
    if (!cartPromise) {
      cartPromise = fetch(CART_ENDPOINT, { credentials: "same-origin" })
        .then((r) => { if (!r.ok) throw new Error("cart"); return r.json(); })
        .finally(() => { cartPromise = null; });
    }
    return cartPromise;
  };

  /* ── Section initialiser ── */
  const initSection = (scope) => {
    const tabs = $(scope, TABS_SELECTOR);
    if (!tabs || tabs.dataset.initialized === "true") return;
    tabs.dataset.initialized = "true";

    const section = tabs.closest(".shopify-section") || scope;
    const { locale = "en-US", currency = "USD" } = tabs.dataset;

    // Elements inside the tabs block
    const tabTriggers = $$(tabs, "[data-tab-trigger]");
    const tabPanels = $$(tabs, "[data-tab-panel]");

    // Elements from sibling blocks — search section-wide
    const variantIdInputs = $$(section, "[data-variant-id-input]");
    const quantityPills = $(section, "[data-quantity-pills]");
    const quantityInput = $(section, "[data-quantity-input]");
    const variantPickerWrap = $(section, "[data-variant-picker-wrap]");
    const atcPrice = $(section, "[data-atc-price]");
    const atcComparePrice = $(section, "[data-atc-compare-price]");
    const atcButton = $(section, "[data-atc-button]");
    const thresholdTrack = $(section, "[data-threshold-track]");
    const thresholdDots = $$(section, "[data-threshold-dot]");
    const thresholdLabels = $$(section, "[data-threshold-label]");
    const stitcherForm = $(section, "[data-metafield-tabs-form]");
    let currentVariantPrice = 0;
    let currentUnitPrice = 0;
    let currentTierQuantity = Number(quantityInput?.value || 1);
    let currentTierDiscount = 0;
    let previewCard = null;

    const getActiveCard = () => $(tabs, "[data-tab-panel].is-active [data-product-card].is-active");
    const getActiveSellingPlans = () => {
      const card = getActiveCard();
      if (!card) return [];
      return getCachedJSON(card, "sellingPlans");
    };
    const getSelectedSellingPlan = ({ fallbackToFirst = false } = {}) => {
      const plans = getActiveSellingPlans();
      if (!plans.length) return null;

      const planInput = document.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
      const planId = planInput?.value;
      if (planId) {
        const matched = plans.find((p) => String(p.id) === String(planId));
        if (matched) return matched;
      }
      return fallbackToFirst ? plans[0] : null;
    };

    const setVisibility = (el, visible) => {
      if (!el) return;
      if ("hidden" in el) el.hidden = !visible;
      if (el.style) el.style.display = visible ? "" : "none";
      if (!visible) el.setAttribute("aria-hidden", "true");
      else el.removeAttribute("aria-hidden");
    };

    const updateRechargeWidgetPrices = () => {
      const widgetRoots = section.querySelectorAll(".recharge-subscription-widget, recharge-subscription-widget, [data-recharge-widget]");
      if (!widgetRoots.length || currentVariantPrice <= 0) return;

      const selectedPlan = getSelectedSellingPlan({ fallbackToFirst: true });
      const originalTotal = currentUnitPrice * currentTierQuantity;
      const oneTimePrice = currentVariantPrice;
      const subPrice = applyPlanDiscount(currentVariantPrice, selectedPlan) ?? currentVariantPrice;
      const oneTimeHasDiscount = currentTierDiscount > 0 && originalTotal > 0;
      const subscriptionHasDiscount = originalTotal > 0 && subPrice < originalTotal;
      let subscriptionSavePercent = 0;
      if (selectedPlan && String(selectedPlan.discount_type) === "percentage") {
        subscriptionSavePercent = Math.max(0, Math.round(Number(selectedPlan.discount_value || 0)));
      } else if (originalTotal > 0 && subPrice < originalTotal) {
        subscriptionSavePercent = Math.max(0, Math.round(((originalTotal - subPrice) / originalTotal) * 100));
      }
      const subscriptionSaveText = subscriptionSavePercent > 0 ? `Save ${subscriptionSavePercent}%` : "";

      const oneTimeText = formatMoney(oneTimePrice, locale, currency);
      const subText = formatMoney(subPrice, locale, currency);
      const compareText = originalTotal > 0 ? formatMoney(originalTotal, locale, currency) : "";

      const containerSelectors = {
        oneTime: [
          '[part~="rc-purchase-option__onetime"]',
          ".rc-purchase-option--onetime",
          '[data-purchase-option="onetime"]',
          '[data-option="onetime"]',
        ],
        sub: [
          '[part~="rc-purchase-option__subscription"]',
          ".rc-purchase-option--subscription",
          '[data-purchase-option="subscription"]',
          '[data-option="subscription"]',
        ],
      };
      const discountPriceSelectors = [
        '[part~="rc-purchase-option__discounted-price"]',
        ".rc-purchase-option__discounted-price",
        ".rc_widget__price",
        '[part~="rc-purchase-option__price"]',
        ".rc-price",
      ];
      const comparePriceSelectors = [
        '[part~="rc-purchase-option__original-price"]',
        ".rc-purchase-option__original-price",
        ".rc_widget__original-price",
        ".rc-price--compare",
      ];

      const findScopedContainers = (root, scopeSelectors, mode) => {
        const exact = scopeSelectors.flatMap((sel) => [...root.querySelectorAll(sel)]);
        if (exact.length) return exact;

        // Fallback: ReCharge markup variants often still expose generic purchase-option rows.
        const genericRows = [...root.querySelectorAll(".rc-purchase-option, [data-purchase-option], [part~='rc-purchase-option']")];
        if (!genericRows.length) return [];

        return genericRows.filter((row) => {
          const txt = (row.textContent || "").toLowerCase();
          if (mode === "oneTime") return txt.includes("one-time") || txt.includes("one time");
          if (mode === "sub") return txt.includes("subscribe") || txt.includes("subscription");
          return false;
        });
      };

      const applyScope = (root, scopeSelectors, priceText, showCompare, mode) => {
        const containers = findScopedContainers(root, scopeSelectors, mode);
        containers.forEach((container) => {
          const pricesWrap = container.querySelector(".rc-purchase-option__prices, [part~='rc-purchase-option__prices']");
          if (pricesWrap) {
            let compareNode = pricesWrap.querySelector(".rc-stitcher-compare");
            if (mode === "oneTime" && showCompare) {
              if (!compareNode) {
                compareNode = document.createElement("span");
                compareNode.className = "rc-stitcher-compare rc-price strike-through text-muted";
                compareNode.setAttribute("part", "rc-purchase-option__original-price");
                pricesWrap.insertBefore(compareNode, pricesWrap.firstChild || null);
              }
              if (compareNode.textContent !== compareText) compareNode.textContent = compareText;
              setVisibility(compareNode, true);
            } else if (compareNode) {
              compareNode.remove();
            }
          }

          discountPriceSelectors.forEach((sel) => {
            container.querySelectorAll(sel).forEach((n) => {
              if (n.textContent !== priceText) n.textContent = priceText;
            });
          });
          comparePriceSelectors.forEach((sel) => {
            container.querySelectorAll(sel).forEach((n) => {
              if (showCompare && n.textContent !== compareText) n.textContent = compareText;
              if (!showCompare && n.textContent !== "") n.textContent = "";
              setVisibility(n, showCompare);
            });
          });

          if (mode === "sub" && subscriptionSaveText) {
            const firstBenefit = container.querySelector(".rc-benefits__list li:first-child, [part~='rc-benefits__list'] li:first-child");
            if (firstBenefit && firstBenefit.textContent.trim() !== subscriptionSaveText) {
              firstBenefit.textContent = ` ${subscriptionSaveText} `;
            }
          }
        });
      };

      widgetRoots.forEach((widget) => {
        applyScope(widget, containerSelectors.oneTime, oneTimeText, oneTimeHasDiscount, "oneTime");
        applyScope(widget, containerSelectors.sub, subText, subscriptionHasDiscount, "sub");

        const hosts = [widget, ...widget.querySelectorAll("*")];
        hosts.forEach((host) => {
          if (!host.shadowRoot) return;
          applyScope(host.shadowRoot, containerSelectors.oneTime, oneTimeText, oneTimeHasDiscount, "oneTime");
          applyScope(host.shadowRoot, containerSelectors.sub, subText, subscriptionHasDiscount, "sub");
        });
      });
    };

    /* ── Threshold ── */
    const updateThreshold = (index) => {
      const maxStep = Math.max(0, thresholdDots.length - 1);
      const step = Number.isNaN(Number(index)) || index < 0 ? -1 : clamp(Number(index), 0, maxStep);
      if (thresholdTrack) {
        const progress = maxStep > 0 && step >= 0 ? (step / maxStep) * 100 : 0;
        thresholdTrack.style.setProperty("--threshold-progress", `${progress}%`);
      }
      thresholdDots.forEach((d, i) => d.classList.toggle("is-active", step >= 0 && i <= step));
      thresholdLabels.forEach((l, i) => l.classList.toggle("is-active", step >= 0 && i <= step));
    };

    const refreshThresholdFromCart = async () => {
      if (!thresholdDots.length) return;
      try {
        const cart = await fetchCart();
        const bottleQty = (cart.items || []).reduce(
          (total, item) => total + (Number(item.quantity || 0) * getBottleMultiplier(item)),
          0
        );
        let step = -1;
        if (bottleQty >= 12) step = 3;
        else if (bottleQty >= 6) step = 2;
        else if (bottleQty >= 2) step = 1;
        else if (bottleQty >= 1) step = 0;
        updateThreshold(step);
      } catch { updateThreshold(-1); }
    };

    let refreshTimer = null, drawerObserver = null;
    const scheduleRefresh = (delay = 0) => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        if (!document.body.contains(tabs)) { drawerObserver?.disconnect(); return; }
        refreshThresholdFromCart();
      }, delay);
    };

    /* ── Selling plan price calc ── */
    const getSellingPlanPrice = () => {
      const plan = getSelectedSellingPlan();
      if (!plan) return null;

      return applyPlanDiscount(currentVariantPrice, plan);
    };

    /* ── ATC price — only write DOM when value actually changes ── */
    const updateAtcPrice = () => {
      if (!atcPrice) return;
      const subPrice = getSellingPlanPrice();
      const next = formatMoney(subPrice != null ? subPrice : currentVariantPrice, locale, currency);
      if (atcPrice.textContent !== next) atcPrice.textContent = next;

      if (atcComparePrice) {
        const originalTotal = currentUnitPrice * currentTierQuantity;
        if (currentTierDiscount > 0 && originalTotal > 0) {
          const compareText = formatMoney(originalTotal, locale, currency);
          if (atcComparePrice.textContent !== compareText) atcComparePrice.textContent = compareText;
          atcComparePrice.hidden = false;
        } else {
          atcComparePrice.hidden = true;
        }
      }

      updateRechargeWidgetPrices();
    };

    const clearSelectionUI = () => {
      previewCard = null;
      currentVariantPrice = 0;
      currentUnitPrice = 0;
      currentTierQuantity = 1;
      currentTierDiscount = 0;
      variantIdInputs.forEach((input) => {
        if (input.value !== "") {
          input.value = "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
      if (quantityPills) quantityPills.innerHTML = "";
      if (quantityInput) quantityInput.value = "1";
      if (variantPickerWrap) variantPickerWrap.hidden = true;
      if (atcPrice) atcPrice.textContent = "";
      if (atcComparePrice) { atcComparePrice.textContent = ""; atcComparePrice.hidden = true; }
      if (atcButton) atcButton.disabled = true;
    };

    /* ── Quantity tier UI update ── */
    const updateQuantityTierUI = (tier) => {
      if (!tier) return;
      currentUnitPrice = Number(tier.unitPrice || 0);
      currentTierQuantity = Number(tier.quantity || 1);
      currentTierDiscount = Number(tier.discount || 0);
      currentVariantPrice = calcTierPrice(currentUnitPrice, currentTierQuantity, currentTierDiscount);

      if (quantityInput && quantityInput.value !== String(currentTierQuantity)) {
        quantityInput.value = String(currentTierQuantity);
      }

      if (quantityPills) {
        quantityPills.querySelectorAll("[data-quantity-pill]").forEach((p) =>
          p.classList.toggle("is-active",
            Number(p.dataset.tierQuantity) === currentTierQuantity &&
            Number(p.dataset.tierDiscount || 0) === currentTierDiscount)
        );
      }
      updateAtcPrice();
      if (atcButton) atcButton.disabled = false;
    };

    /* ── Populate quantity pills using DocumentFragment (single reflow) ── */
    const populateQuantityPills = (card, { markActive = true, syncTier = true, disableAll = false } = {}) => {
      if (!quantityPills) return;
      const tiers = getCachedJSON(card, "quantityTiers");
      const variants = getCachedJSON(card, "variants");
      const firstVariant = variants[0];

      if (variantPickerWrap) {
        variantPickerWrap.hidden = tiers.length === 0;
      }

      // Always point to the first available variant — quantity changes, variant does not
      const variantId = String(firstVariant?.id || "");
      variantIdInputs.forEach((input) => {
        if (input.value !== variantId) {
          input.value = variantId;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });

      const frag = document.createDocumentFragment();
const initialTier = tiers[0];
const variantUnavailable = firstVariant && firstVariant.available === false;

tiers.forEach((tier, i) => {
  const pill = document.createElement("button");

  Object.assign(pill, {
    type: "button",
    className: "pdp-metafield-tabs__variant-pill",
  });

  pill.dataset.quantityPill = "";
  pill.dataset.tierQuantity = tier.quantity;
  pill.dataset.tierDiscount = tier.discount || 0;
  pill.dataset.tierUnitPrice = tier.unitPrice || firstVariant?.price || 0;

  if (disableAll || variantUnavailable) pill.disabled = true;
  if (markActive && !variantUnavailable && i === 0) pill.classList.add("is-active");

  // Add label text
  const labelText = document.createTextNode(tier.label);
  pill.appendChild(labelText);

  // Add discount span (only if discount exists)
  if (tier.discount) {
    const discountSpan = document.createElement("span");
    discountSpan.className = "cs-pill-discount";
    discountSpan.textContent = `Save ${tier.discount}%`;
    pill.appendChild(discountSpan);
  }

  frag.appendChild(pill);
});

      quantityPills.innerHTML = "";
      quantityPills.appendChild(frag);

      if (variantUnavailable) {
        if (atcButton) atcButton.disabled = true;
      } else if (syncTier && initialTier) {
        updateQuantityTierUI({
          ...initialTier,
          unitPrice: initialTier.unitPrice || firstVariant?.price || 0,
        });
      } else if (initialTier) {
        const up = Number(initialTier.unitPrice || firstVariant?.price || 0);
        currentVariantPrice = calcTierPrice(up, initialTier.quantity, initialTier.discount || 0);
        if (atcPrice) atcPrice.textContent = formatMoney(currentVariantPrice, locale, currency);
        if (atcButton) atcButton.disabled = true;
      }
    };

    const previewCardSelection = (card) => {
      if (!card) {
        clearSelectionUI();
        return;
      }
      previewCard = card;
      populateQuantityPills(card, { markActive: false, syncTier: false, disableAll: true });
    };

    /* ── Card selection — instant, no network fetch ── */
    const selectCard = (card) => {
      if (!card) return;
      previewCard = card;
      $$(tabs, "[data-product-card]").forEach((n) => n.classList.remove("is-active"));
      card.classList.add("is-active");
      populateQuantityPills(card);
    };

    /* ── Event delegation on tabs block (tabs + product cards) ── */
    tabs.addEventListener("click", (e) => {
      // Tab trigger
      const trigger = e.target.closest("[data-tab-trigger]");
      if (trigger) {
        const target = trigger.dataset.tabTrigger;
        tabTriggers.forEach((n) => {
          n.classList.toggle("is-active", n === trigger);
          n.setAttribute("aria-selected", n === trigger ? "true" : "false");
        });
        tabPanels.forEach((p) => p.classList.toggle("is-active", p.dataset.tabPanel === target));
        const activeCardInPanel = $(tabs, `[data-tab-panel="${target}"] [data-product-card].is-active`);
        if (activeCardInPanel) {
          selectCard(activeCardInPanel);
        } else {
          previewCardSelection($(tabs, `[data-tab-panel="${target}"] [data-product-card]`));
        }
        return;
      }
      // Product card — if it links to a different product, do section replace; otherwise instant switch
      const card = e.target.closest("[data-product-card]");
      if (card) {
        const url = card.dataset.productUrl;
        url ? replaceMainProductSection(tabs, url) : selectCard(card);
      }
    });

    /* ── Event delegation on section (quantity pills) ── */
    section.addEventListener("click", (e) => {
      const pill = e.target.closest("[data-quantity-pill]");
      if (!pill || pill.disabled) return;
      const card = getActiveCard() || previewCard;
      if (!card || !card.classList.contains("is-active")) return;
      previewCard = card;
      updateQuantityTierUI({
        unitPrice: Number(pill.dataset.tierUnitPrice || 0),
        quantity: Number(pill.dataset.tierQuantity || 1),
        discount: Number(pill.dataset.tierDiscount || 0),
      });
    });

    /* ── Form submission ── */
    if (stitcherForm) {
      stitcherForm.addEventListener("submit", async (e) => {
        e.preventDefault();
        const btn = $(stitcherForm, '[type="submit"]');
        if (btn?.classList.contains("loading")) return;
        btn?.classList.add("loading");
        btn?.setAttribute("aria-disabled", "true");
        try {
          const payload = {};
          new FormData(stitcherForm).forEach((v, k) => { payload[k] = v; });

          // Pick up selling_plan from ReCharge widget if present
          const rcInput = document.querySelector('input[name="selling_plan"]:not([disabled])');
          if (rcInput && rcInput.value) {
            payload.selling_plan = rcInput.value;
          }

          const cartDrawer = $(document, "cart-drawer");
          if (cartDrawer?.getSectionsToRender) {
            payload.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
            payload.sections_url = window.location.pathname;
          }
          const endpoint = window.routes?.cart_add_url || "/cart/add.js";
          const config = typeof window.fetchConfig === "function"
            ? window.fetchConfig("javascript")
            : { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/javascript" } };
          const resp = await fetch(endpoint, { ...config, body: JSON.stringify(payload) });
          const data = await resp.json();
          if (resp.ok && cartDrawer?.renderContents && data.sections) cartDrawer.renderContents(data, true);
          scheduleRefresh(80);
        } catch {} finally {
          btn?.classList.remove("loading");
          btn?.removeAttribute("aria-disabled");
        }
      });
    }

    /* ── Cart drawer observation ── */
    const cartDrawer = $(document, "cart-drawer");
    if (cartDrawer) {
      const drawerMain = $(cartDrawer, "#cart-drawer-main");
      if (drawerMain) {
        drawerObserver = new MutationObserver(() => scheduleRefresh(60));
        drawerObserver.observe(drawerMain, { childList: true, subtree: true });
      }
      const spinnerSel = ".js-quantity-spinner-input";
      const removeSel = ".js-quantity-spinner-increase, .js-quantity-spinner-decrease, cart-remove-button, cart-remove-button button";
      cartDrawer.addEventListener("input", (e) => { if (e.target.closest(spinnerSel)) scheduleRefresh(80); });
      cartDrawer.addEventListener("change", (e) => { if (e.target.closest(spinnerSel)) scheduleRefresh(80); });
      cartDrawer.addEventListener("click", (e) => { if (e.target.closest(removeSel)) scheduleRefresh(180); });
    }

    /* ── ReCharge selling_plan watcher — targeted, not section-wide ── */
    const sellingPlanObserver = new MutationObserver(() => updateAtcPrice());
    const watchSellingPlan = () => {
      const planInput = document.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
      if (planInput) {
        sellingPlanObserver.observe(planInput, { attributes: true, attributeFilter: ["value"] });
        planInput.addEventListener("change", () => updateAtcPrice());
        updateAtcPrice();
      }
    };

    // ReCharge loads async — poll briefly then stop
    const spInterval = setInterval(() => {
      const planInput = document.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
      if (planInput) {
        clearInterval(spInterval);
        watchSellingPlan();
      }
    }, 500);
    setTimeout(() => clearInterval(spInterval), 10000);

    const rcInterval = setInterval(() => {
      const widget = section.querySelector(".recharge-subscription-widget, recharge-subscription-widget, [data-recharge-widget]");
      if (widget) {
        clearInterval(rcInterval);
        updateRechargeWidgetPrices();
      }
    }, 500);
    setTimeout(() => clearInterval(rcInterval), 10000);

    /* ── Init ── */
    selectCard(getActiveCard() || $(tabs, "[data-product-card]"));
    refreshThresholdFromCart();
  };

  /* ── Public API ── */
  window.ProductStitcherTabs = {
    ...(window.ProductStitcherTabs || {}),
    initAll(scope = document) {
      const tabsEls = scope.querySelectorAll(TABS_SELECTOR);
      tabsEls.forEach((el) => {
        const sectionScope = el.closest(".shopify-section") || el.parentElement;
        initSection(sectionScope);
      });
    },
  };

  window.ProductStitcherTabs.initAll(document);
  document.addEventListener("shopify:section:load", (e) => window.ProductStitcherTabs.initAll(e.target));

  /* ── Handle browser back/forward for stitcher navigation ── */
  window.addEventListener("popstate", (e) => {
    if (e.state?.stitcher) {
      const tabs = document.querySelector(TABS_SELECTOR);
      if (tabs) replaceMainProductSection(tabs, window.location.pathname);
    }
  });
})();
